import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { DocumentsService } from '@services/documents.service';
import { ReplaySubject } from 'rxjs';
import { ManagementEntityEventModel } from '@data-layer/models/management';
import { WidgetsService } from '@services/widgets.service';
import { EntrySummaryDutiesModel, EntrySummaryModel, EntrySummaryStatementModel } from '@data-layer/models/entry-summaries';
import {
  enumEntrySummaryDutiesPaymentType,
  enumEntrySummaryState,
  enumEntrySummarySubmissionType,
  enumStatementState,
  enumTransportationMode,
} from '@data-layer/enums/entry-summaries';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { enumBillItemProductCode, enumPaymentState } from '@data-layer/enums/payments';
import { DocumentModel } from '@data-layer/models/documents';
import { enumDocumentType } from '@data-layer/enums/documents';
import { DocumentsRepository, SelfFilerRepository, StatementsRepository } from '@data-layer/repositories';
import { DateHelper } from '@helpers/date.helper';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { CompaniesService } from '@services/companies.service';
import { IPaymentBreakdownItemGroup } from '@interfaces/payment-breakdown-group';
import { BillModel } from '@data-layer/models/payments';
import { UtilsHelper } from '@helpers/utils-helper';
import { filterFeeItems } from '@app/shared/helpers';
import { CompanyModel } from '@data-layer/models/companies';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { IPaymentBreakdownItem } from '@interfaces/payment-breakdown-item';
import { isDefined } from '@services/utils/utils.service';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { enumCompanyPlacement } from '@enum/enumCompanyPlacement';
import { MarineInsuranceAvailabilityService } from '@shared/marine-insurance-shared/services';

@Component({
  selector: 'es-summary-success-header-desktop',
  template: `
    <div [class]="'summary-success ' + className">
      <div class="summary-success-title">
        <div class="summary-success-title-data" *ngIf="!!entrySummary">
          <div class="title-block" *ngIf="entrySummary.referenceNumber">
            <div class="title-block-data">{{ 'LABELS.EEZY_ID' | translate }}</div>
            <div class="title-block-text">
              <strong>{{ entrySummary.referenceNumber }}</strong>
            </div>
          </div>

          <div class="title-block pl-3">
            <div class="title-block-data" *ngIf="entrySummary.entryNumber">{{ 'LABELS.CBP_ID' | translate }}</div>
            <div class="title-block-text" *ngIf="entrySummary.entryNumber">
              <strong>{{ entrySummary.entryNumber }}</strong>
            </div>
          </div>

          <div class="title-block pl-3" *ngIf="entrySummary.sentToProviderDate || entrySummary.receivedByCbpDate">
            <div class="title-block-data">
              {{
                (entrySummary.receivedByCbpDate
                  ? isDeletedStatus
                    ? 'LABELS.DELETED_BY_CBP'
                    : 'STATUS.SECOND_TEXT.RECEIVED_BY_CBP'
                  : 'LABELS.SENT_TO_PROVIDER'
                ) | translate
              }}
            </div>
            <div class="title-block-text">
              <strong>
                {{ (entrySummary.receivedByCbpDate ? entrySummary.receivedByCbpDate : entrySummary.sentToProviderDate) | customDateByUtc }}
              </strong>
            </div>
          </div>
        </div>
        <div class="summary-success-title-actions" *ngIf="!isAdmin">
          <span
            class="summary-success-title-actions-item"
            nz-tooltip
            nzTooltipColor="#ffffff"
            nzTooltipOverlayClassName="tooltip-sm"
            [nzTooltipTitle]="(entrySummary?.hasNewBrokerMessages ? 'LABELS.SEE_MESSAGE' : 'LABELS.MESSAGE_TO_BROKER') | translate"
            (click)="openChat.emit()"
          >
            <div class="summary-success-title-actions-item-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M20.659 9.29271C20.7961 9.18776 21 9.28594 21 9.45182V17.375C21 18.2721 20.2441 19 19.3125 19H4.6875C3.75586 19 3 18.2721 3 17.375V9.45521C3 9.28594 3.20039 9.19115 3.34102 9.29609C4.12852 9.88516 5.17266 10.6333 8.75859 13.1419C9.50039 13.6633 10.752 14.7602 12 14.7534C13.2551 14.7635 14.5312 13.643 15.2449 13.1419C18.8309 10.6333 19.8715 9.88177 20.659 9.29271ZM12 13.6667C12.8156 13.6802 13.9898 12.6781 14.5805 12.2651C19.2457 9.00495 19.6008 8.72057 20.6766 7.90807C20.8805 7.75573 21 7.51875 21 7.26823V6.625C21 5.72786 20.2441 5 19.3125 5H4.6875C3.75586 5 3 5.72786 3 6.625V7.26823C3 7.51875 3.11953 7.75234 3.32344 7.90807C4.39922 8.71719 4.7543 9.00495 9.41953 12.2651C10.0102 12.6781 11.1844 13.6802 12 13.6667Z"
                />
              </svg>
              <span class="broker-message-badge" *ngIf="entrySummary?.hasNewBrokerMessages">1</span>
            </div>

            {{ 'BUTTON.MESSAGE_BROKER' | translate }}
          </span>

          <span class="summary-success-title-actions-item" *ngIf="isShowInsuranceIndicator">
            <div class="summary-success-title-actions-item-icon">
              <app-marine-insurance-indicator
                [isPlain]="true"
                [isOnEntityPage]="true"
                (addInsurance)="addInsurance.emit()"
                [entityReferenceNumber]="entrySummary.referenceNumber"
                entityRedirectPath="/entry-summary"
                [companyPlacement]="enumCompanyPlacement.ENTRY_SUMMARY"
                [entityStatus]="entrySummary.insuranceStatus"
              ></app-marine-insurance-indicator>
            </div>
          </span>

          <span
            class="summary-success-title-actions-item"
            nz-tooltip
            nzTooltipColor="#ffffff"
            nzTooltipOverlayClassName="tooltip-sm"
            [nzTooltipTitle]="'BUTTON.DUPLICATE' | translate"
            (click)="onDuplicateClick()"
          >
            <div class="summary-success-title-actions-item-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M6 0H18C19.103 0 20 0.897 20 2V14H18V2H6V0ZM2 20H14C15.103 20 16 19.103 16 18V6C16 4.897 15.103 4 14 4H2C0.897 4 0 4.897 0 6V18C0 19.103 0.897 20 2 20ZM2 6H14L14.002 18H2V6ZM9 8H7V11H4V13H7V16H9V13H12V11H9V8Z"
                />
              </svg>
            </div>
          </span>

          <span
            class="summary-success-title-actions-item"
            nz-tooltip
            nzTooltipColor="#ffffff"
            nzTooltipOverlayClassName="tooltip-sm"
            [nzTooltipTitle]="'BUTTON.PRINT' | translate"
            (click)="print.emit()"
          >
            <div class="summary-success-title-actions-item-icon">
              <svg width="20" height="18" viewBox="0 0 20 18" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M13.7472 0C14.0426 0 14.335 0.058198 14.6079 0.171271C14.8808 0.284344 15.1288 0.450078 15.3376 0.65901C15.5465 0.867942 15.7122 1.11598 15.8252 1.38896C15.9382 1.66194 15.9964 1.95453 15.9964 2.25V3.003H16.7461C17.6078 3.00353 18.4341 3.34604 19.0436 3.95537C19.653 4.56469 19.9959 5.39103 19.997 6.253L20 12.25C20.0001 12.5453 19.9421 12.8378 19.8292 13.1107C19.7164 13.3836 19.5509 13.6316 19.3422 13.8406C19.1335 14.0495 18.8858 14.2153 18.6131 14.3284C18.3403 14.4416 18.048 14.4999 17.7528 14.5H15.9944V15.75C15.9944 16.3467 15.7574 16.919 15.3356 17.341C14.9138 17.7629 14.3417 18 13.7452 18H6.24781C5.65128 18 5.07919 17.7629 4.65738 17.341C4.23557 16.919 3.9986 16.3467 3.9986 15.75V14.5H2.24921C1.65268 14.5 1.08059 14.2629 0.658779 13.841C0.23697 13.419 0 12.8467 0 12.25V6.254C0 5.39205 0.34229 4.5654 0.95157 3.9559C1.56085 3.34641 2.38721 3.004 3.24886 3.004L3.9976 3.003L3.9986 2.25C3.9986 1.65326 4.23557 1.08097 4.65738 0.65901C5.07919 0.237053 5.65128 0 6.24781 0H13.7472V0ZM13.7452 10.5H6.24781C6.04897 10.5 5.85827 10.579 5.71767 10.7197C5.57707 10.8603 5.49808 11.0511 5.49808 11.25V15.75C5.49808 16.164 5.83396 16.5 6.24781 16.5H13.7452C13.944 16.5 14.1347 16.421 14.2753 16.2803C14.4159 16.1397 14.4949 15.9489 14.4949 15.75V11.25C14.4949 11.0511 14.4159 10.8603 14.2753 10.7197C14.1347 10.579 13.944 10.5 13.7452 10.5V10.5ZM16.7461 4.504H3.24886C2.7849 4.504 2.33993 4.68837 2.01186 5.01656C1.68379 5.34475 1.49948 5.78987 1.49948 6.254V12.25C1.49948 12.664 1.83536 13 2.24921 13H3.9986V11.25C3.9986 10.6533 4.23557 10.081 4.65738 9.65901C5.07919 9.23705 5.65128 9 6.24781 9H13.7452C14.3417 9 14.9138 9.23705 15.3356 9.65901C15.7574 10.081 15.9944 10.6533 15.9944 11.25V13H17.7768C17.971 12.9935 18.1551 12.9117 18.2901 12.772C18.4252 12.6322 18.5006 12.4454 18.5005 12.251L18.4975 6.254C18.4965 5.78985 18.3115 5.34504 17.9833 5.01702C17.655 4.68901 17.2101 4.50453 16.7461 4.504V4.504ZM13.7472 1.5H6.24781C6.04897 1.5 5.85827 1.57902 5.71767 1.71967C5.57707 1.86032 5.49808 2.05109 5.49808 2.25L5.49708 3.003H14.4969V2.25C14.4969 2.05109 14.4179 1.86032 14.2773 1.71967C14.1367 1.57902 13.946 1.5 13.7472 1.5V1.5Z"
                />
              </svg>
            </div>
          </span>

          <span
            class="summary-success-title-actions-item"
            nz-tooltip
            nzTooltipColor="#ffffff"
            nzTooltipOverlayClassName="tooltip-sm"
            *ngIf="!isTruck"
            [nzTooltipTitle]="'LABELS.CREATE_DELIVERY_ORDER' | translate"
            (click)="createDeliveryOrder.emit()"
          >
            <div class="summary-success-title-actions-item-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M19.2727 9.375H16.5455L16.5454 6H3.81818C2.81364 6 2 6.62578 2 7.60417V17.3438H3.81818C3.81818 18.8091 5.04091 20 6.54545 20C8.05 20 9.27273 18.8091 9.27273 17.3438H14.7273C14.7273 18.8091 15.95 20 17.4545 20C18.9591 20 20.1818 18.8091 20.1818 17.3438H22V12.9167L19.2727 9.375ZM6.54545 18.6719C5.79091 18.6719 5.18182 18.0786 5.18182 17.3438C5.18182 16.6089 5.79091 16.0156 6.54545 16.0156C7.3 16.0156 7.90909 16.6089 7.90909 17.3438C7.90909 18.0786 7.3 18.6719 6.54545 18.6719ZM18.8182 10.7031L20.6045 12.9167H16.5455V10.7031H18.8182ZM17.4545 18.6719C16.7 18.6719 16.0909 18.0786 16.0909 17.3438C16.0909 16.6089 16.7 16.0156 17.4545 16.0156C18.2091 16.0156 18.8182 16.6089 18.8182 17.3438C18.8182 18.0786 18.2091 18.6719 17.4545 18.6719Z"
                  fill="currentColor"
                />
              </svg>
            </div>
          </span>
        </div>
      </div>

      <div class="summary-success-body">
        <div
          class="summary-success-body-notifications"
          [ngClass]="{ success: isSuccessNotification, warning: isPaymentPending }"
          *ngIf="isShowNotification || isShowSuccessNotification || isPaymentPending"
        >
          <div class="summary-success-body-notifications-header" *ngIf="isEditMode">
            <h3>{{ notificationHeader | translate }}</h3>

            <span (click)="closeNotification.emit()">
              <i class="fa fa-times"></i>
              {{ notificationHeaderAction | translate }}
            </span>
          </div>
          <div class="summary-success-body-notifications-body">
            <div class="summary-success-body-notifications-body-icon">
              <span [ngClass]="{ 'icon-ic_big_v': isSuccessNotification, 'icon-ic_big_a': isPaymentPending }"></span>
            </div>
            <div class="summary-success-body-notifications-body-text"><p [innerHTML]="notificationBody | translate"></p></div>
          </div>
        </div>

        <div
          class="summary-success-body-duties-and-payment"
          *ngIf="isShowDuties || paymentTotalPrice > 0 || isPaymentDemandExist"
          [ngClass]="{ 'with-border': isShowReleaseNotification || !!summaryBodyText || isShowSupportBtn || isCBPOpen }"
        >
          <div class="w-100 row">
            <div class="col-8">
              <div class="container summary-success-body-duties-and-payment-duties-cols">
                <ng-container *ngIf="isPaymentDemandExist">
                  <p>
                    <br />
                    <span
                      [innerHTML]="
                        (isHasInstantPayment
                          ? 'STATUS.CARD_TEXT.BROKER_PROCESSING_FAILED_PAYMENT_DEMAND_HEADER_INSTANT'
                          : 'STATUS.CARD_TEXT.BROKER_PROCESSING_FAILED_PAYMENT_DEMAND_HEADER_MONTHLY'
                        ) | translate
                      "
                    ></span>
                  </p>
                </ng-container>
                <ng-container *ngIf="isShowDuties">
                  <div class="row">
                    <div class="col-3 summary-success-body-duties-and-payment-duties-cols-item">
                      <h3 class="summary-success-body-duties-and-payment-duties-cols-item-title no-wrap">
                        {{ 'PAYMENT_BREAKDOWN.DUTY_AMOUNT' | translate }}

                        <app-hint-tooltip-icon [hint]="'PAYMENT_BREAKDOWN.DUTY_AMOUNT_HINT' | translate"></app-hint-tooltip-icon>
                      </h3>
                      <p>{{ duties?.dutyAmount | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}</p>
                      <span *ngIf="duties?.dutyAmount !== entrySummary?.statementEntrySummaryDuties?.estimatedDutyAmount">
                        {{
                          entrySummary?.statementEntrySummaryDuties?.estimatedDutyAmount
                            | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2'
                        }}
                        <span *ngIf="!isPayPalMethod">({{ 'LABELS.ESTIMATED' | translate }})</span>
                      </span>
                    </div>

                    <div
                      *ngIf="entrySummary?.duties?.internalRevenueTax"
                      class="col-3 summary-success-body-duties-and-payment-duties-cols-item"
                    >
                      <h3 class="summary-success-body-duties-and-payment-duties-cols-item-title">
                        {{ 'PAYMENT_BREAKDOWN.INTERNAL_REVENUE_TAX' | translate | titlecase }}
                      </h3>
                      <p>{{ entrySummary?.duties?.internalRevenueTax | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}</p>
                    </div>

                    <div class="col-3 summary-success-body-duties-and-payment-duties-cols-item">
                      <h3 class="summary-success-body-duties-and-payment-duties-cols-item-title no-wrap">
                        {{ 'PAYMENT_BREAKDOWN.HARBOR_MAINTENANCE_FEE_SHORT' | translate }}

                        <app-hint-tooltip-icon [hint]="'PAYMENT_BREAKDOWN.HARBOR_MAINTENANCE_FEE_HINT' | translate"></app-hint-tooltip-icon>
                      </h3>
                      <p>{{ duties?.harborMaintenanceFee | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}</p>
                    </div>

                    <div
                      class="col-3 summary-success-body-duties-and-payment-duties-cols-item"
                      *ngIf="duties?.formalMerchandiseProcessingFee"
                    >
                      <h3 class="summary-success-body-duties-and-payment-duties-cols-item-title no-wrap">
                        {{ 'PAYMENT_BREAKDOWN.MERCHANDISE_PROCESSING_FEE_SHORT' | translate }}

                        <app-hint-tooltip-icon
                          [hint]="'PAYMENT_BREAKDOWN.MERCHANDISE_PROCESSING_FEE_HINT' | translate"
                        ></app-hint-tooltip-icon>
                      </h3>
                      <p>{{ duties?.formalMerchandiseProcessingFee | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}</p>
                      <span *ngIf="duties?.formalMerchandiseProcessingFee !== entrySummary?.statementEntrySummaryDuties?.feeAmounts">
                        {{ entrySummary?.statementEntrySummaryDuties?.feeAmounts | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
                        <span *ngIf="!isPayPalMethod">({{ 'LABELS.ESTIMATED' | translate }})</span>
                      </span>
                    </div>

                    <div
                      class="col-3 summary-success-body-duties-and-payment-duties-cols-item"
                      *ngIf="duties?.informalMerchandiseProcessingFee"
                    >
                      <h3 class="summary-success-body-duties-and-payment-duties-cols-item-title no-wrap">
                        {{ 'PAYMENT_BREAKDOWN.MERCHANDISE_PROCESSING_FEE_SHORT' | translate }}

                        <app-hint-tooltip-icon
                          [hint]="'PAYMENT_BREAKDOWN.MERCHANDISE_PROCESSING_FEE_HINT' | translate"
                        ></app-hint-tooltip-icon>
                      </h3>
                      <p>{{ duties?.informalMerchandiseProcessingFee | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}</p>
                      <span *ngIf="duties?.informalMerchandiseProcessingFee !== entrySummary?.statementEntrySummaryDuties?.feeAmounts">
                        {{ entrySummary?.statementEntrySummaryDuties?.feeAmounts | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
                        <span *ngIf="!isPayPalMethod">({{ 'LABELS.ESTIMATED' | translate }})</span>
                      </span>
                    </div>

                    <div
                      class="col-3 summary-success-body-duties-and-payment-duties-cols-item"
                      *ngIf="isPayPalMethod && paymentTotalPrice > 0 && isHasInstantPayment"
                    >
                      <h3 class="summary-success-body-duties-and-payment-duties-cols-item-title no-wrap">
                        {{ 'PAYMENT_BREAKDOWN.PAYPAL_FEE' | translate }}

                        <app-hint-tooltip-icon [hint]="'PAYMENT_BREAKDOWN.PAYPAL_FEE_HINT' | translate"></app-hint-tooltip-icon>
                      </h3>
                      <p>{{ collectionsFee | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}</p>
                    </div>
                  </div>

                  <div class="row" *ngIf="!isHasInstantPayment && paymentTotalPrice > 0">
                    <div class="col-12">
                      <p>
                        <br />
                        <span [innerHTML]="'STATUS.CARD_TEXT.BROKER_PROCESSING_FAILED_PAYMENT_DEMAND_HEADER_MONTHLY' | translate"></span>
                      </p>
                    </div>
                  </div>
                </ng-container>
              </div>
            </div>

            <div class="col-4">
              <div class="summary-success-body-duties-and-payment-breakdown">
                <div
                  class="summary-success-body-duties-and-payment-breakdown-rectangle"
                  [ngClass]="{ 'mb-4': paymentTotalPrice > 0 }"
                  *ngIf="isShowDuties"
                >
                  <div class="summary-success-body-duties-and-payment-breakdown-left">
                    <h4>{{ 'PAYMENT_BREAKDOWN.TOTAL_DUTIES_AND_FEES' | translate }}</h4>
                    <span>{{ duties?.totalDue | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}</span>
                  </div>
                  <div class="summary-success-body-duties-and-payment-breakdown-right">
                    <span
                      class="payment-label payed"
                      *ngIf="
                        entrySummary.dutiesPaymentState === enumPaymentState.SUCCESS ||
                        entrySummary.dutiesPaymentState === enumPaymentState.DELAYED
                      "
                    >
                      <i class="fa fa-check mr-1"></i>
                      {{
                        entrySummary.dutiesPaymentState === enumPaymentState.DELAYED
                          ? ('PAYMENT_STATE.CONFIRMED' | translate | uppercase)
                          : ('LABELS.PAID' | translate)
                      }}
                    </span>

                    <span
                      class="payment-label payed"
                      *ngIf="entrySummary?.statement?.state === enumESStatementState.AUTHORIZATION_COMPLETED"
                    >
                      <i class="fa fa-check mr-1"></i>
                      {{ 'STATUS.HINT_TEXT.SENDING_PENDING' | translate }}
                    </span>

                    <span
                      class="payment-label not-payed"
                      *ngIf="
                        entrySummary.dutiesPaymentState === enumPaymentState.CREATED &&
                        entrySummary.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.ACH
                      "
                    >
                      {{ 'STATUS.HINT_TEXT.PAYMENT_VIA_ACH' | translate }}
                    </span>

                    <span *ngIf="entrySummary?.statement?.state === enumESStatementState.CREATED">
                      <button class="btn btn-primary" [disabled]="isLoadingAuth" (click)="isStatementModalOpen = true">
                        {{ 'BUTTON.AUTHORIZE_STATEMENT' | translate }}
                      </button>
                    </span>
                  </div>
                </div>

                <div
                  class="summary-success-body-duties-and-payment-breakdown-rectangle"
                  *ngIf="paymentTotalPrice > 0 && (isHasInstantPayment || isHasMonthlyPayment)"
                >
                  <div class="card-payment-content">
                    <ng-template ngFor [ngForOf]="paymentBreakdown" let-paymentGroupItem>
                      <div [class.card-payment-item-group]="paymentGroupItem.discountGroupId">
                        <div
                          class="card-payment-item"
                          [ngClass]="{ header: paymentItem.type === 'header' }"
                          *ngFor="let paymentItem of paymentGroupItem.items"
                        >
                          <div class="card-payment-item-header" [ngClass]="{ 'line-through': paymentItem.priceType === 'canceled' }">
                            <span>
                              {{
                                (paymentItem.header | translate) +
                                  (paymentItem.quantity > 1 ? ' (x' + paymentItem.quantity + ')' : '') +
                                  (paymentItem.type === 'header' ? ':' : '')
                              }}
                            </span>
                            <ng-template [ngIf]="paymentItem.notes">: {{ paymentItem.notes }}</ng-template>
                          </div>
                          <div *ngIf="paymentItem.price !== null && paymentItem.price !== undefined" class="card-payment-item-price">
                            <span class="canceled" *ngIf="paymentItem.priceType === 'canceled'">
                              {{ 'STATUS_MANUAL.CANCELED' | translate }}
                            </span>
                            <span [ngClass]="{ 'line-through': paymentItem.priceType === 'canceled' }">
                              {{ paymentItem.quantity * paymentItem.price | currency : 'USD' : 'symbol' : '1.2-2' }}
                            </span>
                          </div>
                          <div
                            *ngIf="paymentItem.hint"
                            class="card-payment-item-hint"
                            [innerHTML]="'*' + (paymentItem.hint | translate)"
                          ></div>
                        </div>
                      </div>
                    </ng-template>
                    <div class="card-payment-total">
                      <div class="card-payment-item-header">
                        {{ 'LABELS.TOTAL_PAYMENT' | translate }}
                      </div>
                      <div class="card-payment-item-price">
                        {{ paymentTotalPrice | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
                      </div>

                      <div class="w-100 text-center mt-3" *ngIf="!isAdmin">
                        <ng-container *ngIf="isShowPaymentBtn">
                          <payment-button [referenceNumber]="entrySummary.referenceNumber"></payment-button>
                        </ng-container>
                        <ng-container *ngIf="!isShowPaymentBtn">
                          <button type="button" class="btn btn-primary" [disabled]="isLoading" (click)="confirmDuties.emit()">
                            {{ 'CONFIRM_PAYMENT' | translate }}
                          </button>
                        </ng-container>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="summary-success-body-release-notifications" *ngIf="isShowReleaseNotification">
          <div class="summary-success-body-release-notifications-header">
            <span class="icon icon-ic_big_v mr-3"></span>
            <span>{{ 'STATUS.CARD_TEXT.APPROVED_ES_DESKTOP' | translate }}</span>
          </div>

          <div class="summary-success-body-release-notifications-body">
            <h2>{{ 'LABELS.NEXT_STEP' | translate }}:</h2>

            <ol>
              <li>
                <span class="pr-2">{{ 'LABELS.APPROVED_ES_STEP_1_PREFIX' | translate }}</span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M19.2727 9.375H16.5455L16.5454 6H3.81818C2.81364 6 2 6.62578 2 7.60417V17.3438H3.81818C3.81818 18.8091 5.04091 20 6.54545 20C8.05 20 9.27273 18.8091 9.27273 17.3438H14.7273C14.7273 18.8091 15.95 20 17.4545 20C18.9591 20 20.1818 18.8091 20.1818 17.3438H22V12.9167L19.2727 9.375ZM6.54545 18.6719C5.79091 18.6719 5.18182 18.0786 5.18182 17.3438C5.18182 16.6089 5.79091 16.0156 6.54545 16.0156C7.3 16.0156 7.90909 16.6089 7.90909 17.3438C7.90909 18.0786 7.3 18.6719 6.54545 18.6719ZM18.8182 10.7031L20.6045 12.9167H16.5455V10.7031H18.8182ZM17.4545 18.6719C16.7 18.6719 16.0909 18.0786 16.0909 17.3438C16.0909 16.6089 16.7 16.0156 17.4545 16.0156C18.2091 16.0156 18.8182 16.6089 18.8182 17.3438C18.8182 18.0786 18.2091 18.6719 17.4545 18.6719Z"
                    fill="currentColor"
                  />
                </svg>
                <span class="pl-2">{{ 'LABELS.APPROVED_ES_STEP_1_SUFFIX' | translate }}</span>
              </li>

              <li>
                <span>{{ 'LABELS.APPROVED_ES_STEP_2' | translate }}</span>
              </li>
            </ol>

            <div class="result-files">
              <div class="result-files-item" *ngFor="let document of otherAttachedDocuments" (click)="downloadDocument(document)">
                <span class="result-files-item-icon">
                  <i class="fa fa-paperclip"></i>
                </span>
                <span class="result-files-item-name">{{ 'DOCUMENT_TYPE.' + enumDocumentType[document.type] | translate }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="summary-success-body-text">
          <div
            class="summary-success-body-text-description"
            *ngIf="!!summaryBodyText || isShowSupportBtn"
            [ngClass]="{ 'p-0': isPaymentPending }"
          >
            <div class="summary-success-body-text-description-t">
              <span [innerHTML]="summaryBodyText | translate : { referenceNumber: entrySummary.referenceNumber }"></span>
            </div>
            <span class="btn-contact-support" *ngIf="isShowSupportBtn" (click)="contactSupport()">
              <svg width="18" height="14" viewBox="0 0 18 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M1.28746e-05 4.5L0 14H13.5C15.9853 14 18 11.3895 18 8.90425V4.5C18 2.01472 15.9853 0 13.5 0H4.50001C2.01473 0 1.28746e-05 2.01472 1.28746e-05 4.5ZM3 5C3 4.44772 3.44772 4 4 4H14C14.5523 4 15 4.44772 15 5C15 5.55228 14.5523 6 14 6H4C3.44772 6 3 5.55228 3 5ZM4 8C3.44772 8 3 8.44771 3 9C3 9.55229 3.44772 10 4 10H11C11.5523 10 12 9.55229 12 9C12 8.44771 11.5523 8 11 8H4Z"
                  fill="currentColor"
                />
              </svg>

              {{ 'BUTTON.CONTACT_SUPPORT_ISF_ES' | translate }}
            </span>
          </div>

          <div class="summary-success-body-cbp-log" *ngIf="isCBPOpen">
            <es-summary-cbp-log-desktop [messageList]="cbpLogMessages" (close)="openCBP()"></es-summary-cbp-log-desktop>
          </div>
        </div>
      </div>
    </div>

    <a #link class="d-none" download></a>

    <custom-modal
      modalClass="preliminary-statement"
      size="lg"
      [open]="isStatementModalOpen"
      [header]="!!statement?.paidDate ? 'MENU.FINAL_STATEMENT_AUTHORIZATION' : 'MENU.PRELIMINARY_STATEMENT_AUTHORIZATION'"
      (openChange)="isStatementModalOpen = !isStatementModalOpen"
    >
      <div>
        <p [innerHTML]="'LABELS.AUTHORIZED_STATEMENT_HINT' | translate : { statement: statement?.statementNumber }"></p>

        <div class="preliminary-statement-notification">
          <span class="icon icon-ic_big_v mr-3"></span>
          <h3>{{ 'LABELS.AUTHORIZED_STATEMENT_NOTIFICATION' | translate }}</h3>
        </div>
        <div class="preliminary-statement-duties-item" *ngFor="let entry of statement?.entries">
          <h4>{{ entry.referenceNumber }}</h4>

          <div class="preliminary-statement-duties-cols">
            <div class="preliminary-statement-duties-cols-item">
              <h4>
                {{ 'PAYMENT_BREAKDOWN.DUTY_AMOUNT' | translate }}

                <app-hint-tooltip-icon [hint]="'PAYMENT_BREAKDOWN.DUTY_AMOUNT_HINT' | translate"></app-hint-tooltip-icon>
              </h4>
              <p>{{ entry.duties?.dutyAmount | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}</p>
              <span *ngIf="entry.duties?.dutyAmount !== entry?.statementEntrySummaryDuties?.estimatedDutyAmount">
                {{ entry?.statementEntrySummaryDuties?.estimatedDutyAmount | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
                <span *ngIf="!isPayPalMethod">({{ 'LABELS.ESTIMATED' | translate }})</span>
              </span>
            </div>

            <div class="preliminary-statement-duties-cols-item" *ngIf="duties?.harborMaintenanceFee">
              <h4>
                {{ 'PAYMENT_BREAKDOWN.HARBOR_MAINTENANCE_FEE_SHORT' | translate }}

                <app-hint-tooltip-icon [hint]="'PAYMENT_BREAKDOWN.HARBOR_MAINTENANCE_FEE_HINT' | translate"></app-hint-tooltip-icon>
              </h4>
              <p>{{ entry.duties?.harborMaintenanceFee | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}</p>
            </div>

            <div class="preliminary-statement-duties-cols-item" *ngIf="duties?.formalMerchandiseProcessingFee">
              <h4>
                {{ 'PAYMENT_BREAKDOWN.MERCHANDISE_PROCESSING_FEE_SHORT' | translate }}

                <app-hint-tooltip-icon [hint]="'PAYMENT_BREAKDOWN.MERCHANDISE_PROCESSING_FEE_HINT' | translate"></app-hint-tooltip-icon>
              </h4>
              <p>{{ entry.duties?.formalMerchandiseProcessingFee | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}</p>
            </div>

            <div class="preliminary-statement-duties-cols-item" *ngIf="duties?.formalMerchandiseProcessingFee">
              <h4>
                {{ 'PAYMENT_BREAKDOWN.TOTAL_DUTIES_AND_FEES' | translate }}

                <app-hint-tooltip-icon [hint]="'PAYMENT_BREAKDOWN.MERCHANDISE_PROCESSING_FEE_HINT' | translate"></app-hint-tooltip-icon>
              </h4>
              <p>{{ entry.duties?.totalDue | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}</p>
            </div>
          </div>
        </div>

        <div class="preliminary-statement-duties-cols-total">
          <div class="preliminary-statement-duties-cols-total-label">
            {{ 'LABELS.TOTAL_AMOUNT_DUE' | translate }}
          </div>
          <div class="preliminary-statement-duties-cols-total-value">
            {{ statement?.amount | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
          </div>
        </div>

        <p class="mt-4" [innerHTML]="'LABELS.CONTACT_BROKER_DIRECTLY' | translate"></p>
      </div>

      <div class="w-100 mt-4 text-center">
        <button footer-action type="button" class="btn btn-primary" [disabled]="isLoadingAuth" (click)="authorizePayment()">
          {{ 'BUTTON.AUTHORIZE_STATEMENT' | translate }}
        </button>
      </div>
    </custom-modal>

    <poa-required-modal
      [(open)]="showPoaRequiredModal"
      (discard)="onPoaModalDiscard()"
      (goToPoa)="onPoaModalGoToPoa()"
    ></poa-required-modal>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./es-summary-success-header-desktop.component.scss'],
})
export class EsSummarySuccessHeaderDesktopComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public className: string;
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public cbpLogMessages: ManagementEntityEventModel[];
  @Input() public duties: EntrySummaryDutiesModel;
  @Input() public attachedDocuments: DocumentModel[] = [];
  @Input() public paymentBreakdown: IPaymentBreakdownItemGroup[] = [];
  @Input() public isEditMode: boolean;
  @Input() public isSent: boolean;
  @Input() public isAdmin: boolean;
  @Input() public isLoading: boolean;
  @Input() public isMonthlyInstantDuties: boolean;
  /** Opt-in: only a host that binds (addInsurance) may render the shield, otherwise the click would be swallowed. */
  @Input() public isShowInsuranceIcon: boolean = false;
  @Output() public messageToBroker: EventEmitter<void> = new EventEmitter<void>();
  @Output() public duplicate: EventEmitter<void> = new EventEmitter<void>();
  @Output() public print: EventEmitter<void> = new EventEmitter<void>();
  @Output() public createDeliveryOrder: EventEmitter<void> = new EventEmitter<void>();
  @Output() public closeNotification: EventEmitter<void> = new EventEmitter<void>();

  @Output() public cancelPayment: EventEmitter<any> = new EventEmitter<any>();
  @Output() public successPayment: EventEmitter<{ state: enumPaymentState; bill: BillModel }> = new EventEmitter<{
    state: enumPaymentState;
    bill: BillModel;
  }>();
  @Output() public errorPayment: EventEmitter<any> = new EventEmitter<any>();
  @Output() public errorCustomPayment: EventEmitter<any> = new EventEmitter<any>();
  @Output() public addToCard: EventEmitter<any> = new EventEmitter<any>();
  @Output() public openChat: EventEmitter<void> = new EventEmitter<void>();
  @Output() public confirmDuties: EventEmitter<void> = new EventEmitter<void>();
  @Output() public addInsurance: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('link', { static: false }) public linkRef: ElementRef;

  public enumCompanyPlacement: typeof enumCompanyPlacement = enumCompanyPlacement;
  public isCBPOpen: boolean;
  public DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;
  public enumPaymentState = enumPaymentState;
  public enumDocumentType = enumDocumentType;
  public enumESStatementState = enumStatementState;
  public enumEntrySummaryDutiesPaymentType = enumEntrySummaryDutiesPaymentType;

  public viewAttachedDocuments: DocumentModel[] = [];
  public otherAttachedDocuments: DocumentModel[] = [];

  public statement: EntrySummaryStatementModel;
  public payerUnitNumber: string;
  public isCanAuthorize: boolean = false;

  public isStatementModalOpen: boolean = false;

  public isLoadingAuth: boolean;

  public company: CompanyModel;

  public showPoaRequiredModal: boolean = false;

  private readonly docTypesForCheckingLastVersion: enumDocumentType[] = [
    enumDocumentType.CARGO_RELEASE_FORM_3461,
    enumDocumentType.CBP_FORM_7501,
    enumDocumentType.DELIVERY_ORDER,
  ];

  private lastDocumentForType: Map<enumDocumentType, DocumentModel> = new Map();
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private widgetsService: WidgetsService,
    private documentsRepository: DocumentsRepository,
    private statementsRepository: StatementsRepository,
    private selfFilerRepository: SelfFilerRepository,
    private companiesService: CompaniesService,
    private entrySummaryStateService: EntrySummaryStateService,
    private documentsService: DocumentsService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private marineInsuranceAvailabilityService: MarineInsuranceAvailabilityService,
  ) {}

  public onDuplicateClick(): void {
    this.documentsService
      .canActWithoutPoaPrompt$()
      .pipe(take(1))
      .subscribe((canProceed: boolean) => {
        if (canProceed) {
          this.duplicate.emit();
        } else {
          this.showPoaRequiredModal = true;
          this.cdr.markForCheck();
        }
      });
  }

  public onPoaModalDiscard(): void {
    this.showPoaRequiredModal = false;
    this.cdr.markForCheck();
  }

  public onPoaModalGoToPoa(): void {
    const companyId: number = this.companiesService.getCompany()?.id;
    if (!companyId) {
      return;
    }
    this.router.navigate(['/companies', 'account-settings', 'company-view', companyId], {
      queryParams: { isEditViewOpen: true },
      fragment: 'power-of-attorney',
    });
  }

  public ngOnInit(): void {
    this.getStatement(this.entrySummary?.statement?.id);

    this.filterItems();

    this.company = this.companiesService.getCompany();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.attachedDocuments) {
      this.viewAttachedDocuments = this.getOnlyLastVersion();
      this.findOtherAttachedDocuments();
    }

    if (changes?.paymentBreakdown) {
      this.filterItems();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public filterItems(): void {
    filterFeeItems(this.paymentBreakdown);
  }

  public get isShowInsuranceIndicator(): boolean {
    if (!this.isShowInsuranceIcon) {
      return false;
    }

    return this.marineInsuranceAvailabilityService.isShowIndicatorForEntrySummary(this.entrySummary);
  }

  public get isDeletedStatus(): boolean {
    return [enumEntrySummaryState.DELETED, enumEntrySummaryState.CBP_DELETED].includes(this.entrySummary.state);
  }

  public get summaryBodyText(): string {
    if (!this.entrySummary) {
      return;
    }

    switch (this.entrySummary.state) {
      case enumEntrySummaryState.SENT_TO_PROVIDER:
      case enumEntrySummaryState.BROKER_PROCESSING_PENDING:
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHING:
        return 'STATUS_ES.DESCRIPTION.AWAITING_RESPONSE';
      case enumEntrySummaryState.BROKER_PROCESSING_FAILED:
        if (this.isPaymentDemandExist) {
          return this.isHasInstantPayment ? 'STATUS.CARD_TEXT.BROKER_PROCESSING_FAILED_PAYMENT_DEMAND_HINT' : '';
        } else {
          return 'STATUS.CARD_TEXT.BROKER_PROCESSING_FAILED';
        }
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHED:
        if (this.isPaymentPending || enumPaymentState.PENDING === this.entrySummary.dutiesPaymentState) {
          return 'YOUR_FILING_IS_ON_HOLD';
        }
        return 'STATUS_ES.DESCRIPTION.BROKER_PROCESSING_FINISHED';
      case enumEntrySummaryState.BROKER_PROCESSING_CARGO_RELEASE_PENDING:
        return 'STATUS_ES.DESCRIPTION.BROKER_PROCESSING_CARGO_RELEASE_PENDING';
      case enumEntrySummaryState.AWAITING_ARRIVAL:
        return 'STATUS_ES.DESCRIPTION.AWAITING_ARRIVAL';
      case enumEntrySummaryState.SENT_TO_CBP:
        return 'STATUS_ES.DESCRIPTION.SENT_TO_CBP';
      case enumEntrySummaryState.ACCEPTED:
        return 'STATUS_ES.DESCRIPTION.ACCEPTED';
      case enumEntrySummaryState.DELETED:
        return 'STATUS_ES.DESCRIPTION.DELETED';
      case enumEntrySummaryState.UNRECOVERABLE:
        return 'STATUS_ES.DESCRIPTION.UNRECOVERABLE';
      case enumEntrySummaryState.CUSTOM:
        return this.entrySummary.customState?.description;

      default:
        return '';
    }
  }

  public get isShowSupportBtn(): boolean {
    if (!this.entrySummary) {
      return;
    }

    switch (this.entrySummary.state) {
      case enumEntrySummaryState.UNRECOVERABLE:
      case enumEntrySummaryState.NOT_VALID_PROVIDER:
      case enumEntrySummaryState.NO_BILL_ON_FILE:
      case enumEntrySummaryState.REJECTED:
        return true;

      default:
        return false;
    }
  }

  public get isShowDuties(): boolean {
    if (!this.entrySummary?.duties) {
      return;
    }

    switch (this.entrySummary.state) {
      case enumEntrySummaryState.DUTY_CALCULATION_COMPLETE:
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHED:
      case enumEntrySummaryState.BROKER_PROCESSING_CARGO_RELEASE_PENDING:
      case enumEntrySummaryState.AWAITING_ARRIVAL:
      case enumEntrySummaryState.SENT_TO_CBP:
      case enumEntrySummaryState.ACCEPTED:
      case enumEntrySummaryState.APPROVED:
        return true;

      default:
        return false;
    }
  }

  public get isPaymentDemandExist(): boolean {
    return this.entrySummary?.state === enumEntrySummaryState.BROKER_PROCESSING_FAILED && this.paymentTotalPrice > 0;
  }

  public openCBP(): void {
    this.isCBPOpen = !this.isCBPOpen;
  }

  public contactSupport() {
    const { referenceNumber } = this.entrySummary;
    this.widgetsService.trackData('Contact team:', { referenceNumber });
    this.widgetsService.openWidget(['es']);
  }

  public get isSuccessNotification(): boolean {
    return this.isShowSuccessNotification;
  }

  public get isPaymentPending(): boolean {
    return this.entrySummary?.paymentState === enumPaymentState.PENDING;
  }

  public get notificationHeader(): string {
    return this.isEditMode ? 'LABELS.EDIT_MODE' : '';
  }

  public get notificationHeaderAction(): string {
    return this.isEditMode ? 'BUTTON.EXIT_EDIT_MODE' : '';
  }

  public get notificationBody(): string {
    return this.isEditMode ? 'LABELS.EDIT_MODE_HINT' : this.isPaymentPending ? 'YOUR_FILING_IS_ON_HOLD' : 'LABELS.ES_SENT_NOTIFICATION';
  }

  public get isShowNotification(): boolean {
    return this.isEditMode;
  }

  public get isShowSuccessNotification(): boolean {
    return (
      !this.isEditMode &&
      [
        enumEntrySummaryState.SENT_TO_PROVIDER,
        enumEntrySummaryState.VALIDATED_AT_PROVIDER,
        enumEntrySummaryState.PROVIDER_SEND_PENDING,
        enumEntrySummaryState.BROKER_PROCESSING_PENDING,
      ].includes(this.entrySummary?.state)
    );
  }

  public get isShowReleaseNotification(): boolean {
    return [enumEntrySummaryState.APPROVED].includes(this.entrySummary?.state);
  }

  public get isPayPalMethod(): boolean {
    return this.entrySummary?.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.INSTANT;
  }

  public downloadDocument(document: DocumentModel): void {
    this.documentsRepository
      .getDownloadLink(document.id)
      .pipe(take(1))
      .subscribe((linkData) => {
        this.linkRef.nativeElement.href = linkData.downloadUrl;
        this.linkRef.nativeElement.click();
      });
  }

  public authorizePayment(): void {
    this.isLoadingAuth = true;

    this.statementsRepository
      .authorized(this.statement.id, this.payerUnitNumber)
      .pipe(
        finalize(() => (this.isLoadingAuth = false)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.getStatement(this.statement.id);
        this.isStatementModalOpen = false;
      });
  }

  public get paymentTotalPrice(): number {
    return this.paymentBreakdown
      .reduce((paymentItems, paymentGroupItems) => [...paymentItems, ...paymentGroupItems.items], [])
      .filter((paymentItem) => paymentItem.priceType !== 'canceled')
      .filter((paymentItem) => paymentItem.type === 'price')
      .reduce((prevValue, currItem) => prevValue + currItem.quantity * currItem.price, 0);
  }

  public get collectionsFee(): number {
    return this.paymentBreakdown
      .reduce((paymentItems, paymentGroupItems) => [...paymentItems, ...paymentGroupItems.items], [])
      .find((item) => item.code === enumBillItemProductCode.COLLECTION_FEE)?.price;
  }

  public get isHasInstantPayment(): boolean {
    return (this.paymentBreakdown || [])
      .reduce(
        (paymentItems: IPaymentBreakdownItemGroup[], paymentGroupItems: IPaymentBreakdownItemGroup) => [
          ...paymentItems,
          ...paymentGroupItems.items,
        ],
        [],
      )
      .some((bill: IPaymentBreakdownItem) => bill?.paymentType === enumCompanyPaymentType.INSTANT);
  }

  public get isHasMonthlyPayment(): boolean {
    return (this.paymentBreakdown || [])
      .reduce(
        (paymentItems: IPaymentBreakdownItemGroup[], paymentGroupItems: IPaymentBreakdownItemGroup) => [
          ...paymentItems,
          ...paymentGroupItems.items,
        ],
        [],
      )
      .some((bill: IPaymentBreakdownItem) => bill?.paymentType === enumCompanyPaymentType.MONTHLY);
  }

  public get isTruck(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.TRUCK;
  }

  public get isCustomsInstantType(): boolean {
    return this.company?.customsPaymentType === enumCompanyPaymentType.INSTANT;
  }

  public get isShowPaymentBtn(): boolean {
    if ([enumPaymentState.SUCCESS, enumPaymentState.DELAYED].includes(this.entrySummary?.dutiesPaymentState)) {
      this.entrySummaryStateService.isShowPaymentBtn$.next(this.isCustomsInstantType);
      return this.isCustomsInstantType;
    } else {
      const isShowButton = isDefined(this.entrySummary?.dutiesPaymentState) ? !this.isMonthlyInstantDuties : this.isHasInstantPayment;
      this.entrySummaryStateService.isShowPaymentBtn$.next(isShowButton);
      return isShowButton;
    }
  }

  private getStatement(statementId: number) {
    if (statementId) {
      this.statementsRepository
        .get(statementId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((statement) => {
          this.statement = statement;
          this.isCanAuthorize = statement.entries.every((entry) => entry.state === enumEntrySummaryState.APPROVED);
          if (!this.statement.entries.length) {
            throw new Error('Can not find entries on statement or submissionType/PUN invalid');
            return;
          }
          if (UtilsHelper.uniq<enumEntrySummarySubmissionType>(this.statement.entries.map((e) => e.submissionType)).length > 1) {
            throw new Error('SubmissionType invalid');
            return;
          }
          if (UtilsHelper.uniq<string>(this.statement.entries.map((e) => e.payerUnitNumber)).length > 1) {
            throw new Error('PUN invalid');
            return;
          }
          const { submissionType, payerUnitNumber } = this.statement.entries[0];
          if (submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED) {
            this.payerUnitNumber = payerUnitNumber;
          } else {
            this.getPunFromCompany();
          }
        });
    }
  }

  private getPunFromCompany() {
    this.selfFilerRepository
      .getSelfFiler(this.company?.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((info) => {
        this.payerUnitNumber = info.achMethodDetails.paymentUnitNumber;
      });
  }

  private getOnlyLastVersion() {
    return this.attachedDocuments.filter((doc) =>
      this.docTypesForCheckingLastVersion.indexOf(doc.type) !== -1 ? this.isLastVersion(doc) : true,
    );
  }

  private isLastVersion(doc: DocumentModel): boolean {
    return this.getLastVersion(doc.type, doc.relatedEntityId).id === doc.id;
  }

  private getLastVersion(type: enumDocumentType, relatedEntityId: number): DocumentModel {
    if (this.lastDocumentForType.has(type)) {
      return this.lastDocumentForType.get(type);
    }
    const result: DocumentModel = UtilsHelper.maxElementBy<DocumentModel, string>(
      this.attachedDocuments.filter((doc: DocumentModel) => doc.type === type && doc.relatedEntityId === relatedEntityId),
      'uploadedDate',
      DateHelper.getTime,
    );
    this.lastDocumentForType.set(type, result);
    return result;
  }

  private findOtherAttachedDocuments() {
    this.otherAttachedDocuments = [];
    this.viewAttachedDocuments.forEach((document) => {
      const relatedEntity = this.entrySummary;

      if (
        relatedEntity &&
        relatedEntity.id === this.entrySummary.id &&
        [enumDocumentType.CBP_FORM_7501, enumDocumentType.CARGO_RELEASE_FORM_3461, enumDocumentType.CARGO_RELEASE_FILE].includes(
          document.type,
        )
      ) {
        this.otherAttachedDocuments.push(document);
      }
    });
  }
}
